/**
 * HHMS MIS — API Service Layer
 * Centralized Axios client with JWT auth, auto-refresh, and error handling.
 *
 * Base URL: see services/apiBase.js (same-origin /api/v1 in prod; :9000 in dev)
 *
 * All endpoints return: { success, data, meta?, message? }
 */
import axios from 'axios';
import { refreshAccessToken } from './refreshClient';
import { API_ORIGIN } from './apiBase';

// API origin resolution lives in apiBase.js: same-origin in a production build
// (served behind nginx TLS), or the :9000 dev server on the page's host in dev.
const BASE_URL = `${API_ORIGIN}/api/v1`;

const api = axios.create({ baseURL: BASE_URL, timeout: 15000 });

// ── Request interceptor — attach JWT ──────────────────────────────────────────
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ── Response interceptor — auto-refresh on 401 ───────────────────────────────
api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const original = err.config;

    // FastAPI 422 validation errors arrive as an array of objects — rewrite
    // to a readable string so pages rendering `detail` can never crash React.
    const rawDetail = err.response?.data?.detail;
    if (rawDetail !== undefined && typeof rawDetail !== 'string') {
      try {
        err.response.data.detail = Array.isArray(rawDetail)
          ? rawDetail.map((d) => {
              if (typeof d === 'string') return d;
              const field = Array.isArray(d?.loc) ? d.loc.filter((p) => p !== 'body').join('.') : '';
              return field ? `${field}: ${d?.msg || 'invalid'}` : (d?.msg || JSON.stringify(d));
            }).join(' · ')
          : (rawDetail?.msg || rawDetail?.message || JSON.stringify(rawDetail));
      } catch { /* noop */ }
    }

    if (err.response?.status === 401 && !original._retry) {
      original._retry = true;
      try {
        // Single-flight: concurrent 401s share one refresh so the single-use
        // (rotated) refresh token is never double-spent.
        const newToken = await refreshAccessToken(`${BASE_URL}/auth/refresh`);
        if (newToken) {
          original.headers.Authorization = `Bearer ${newToken}`;
          return api(original);
        }
      } catch {
        localStorage.clear();
        window.location.href = '/login';
      }
    }
    return Promise.reject(err);
  }
);

/** Unwrap the standard { success, data, meta } envelope */
const unwrap = (res) => res.data;

export default api;

// ── Identity & Auth ───────────────────────────────────────────────────────────
export const authAPI = {
  login:           (email, password)    => api.post('/auth/login', { email, password }).then(r => r.data),
  refresh:         (refresh_token)      => api.post('/auth/refresh', { refresh_token }).then(unwrap),
  me:              ()                   => api.get('/auth/me').then(unwrap),
  changePassword:  (data)               => api.post('/auth/change-password', data).then(unwrap),
  forgotPassword:  (email)              => api.post('/auth/password/reset/init', { email }).then(unwrap),
  resetPassword:   (email, code, new_password) => api.post('/auth/password/reset/confirm', { email, code, new_password }).then(unwrap),
  // Sessions & sign-out (M2 — Identity & Access)
  logout:          ()                   => api.post('/auth/logout').then(unwrap),
  // Used during sign-out, after storage is cleared — passes the token explicitly.
  logoutWithToken: (token)              => axios.post(`${BASE_URL}/auth/logout`, {}, { headers: { Authorization: `Bearer ${token}` } }),
  logoutAll:       ()                   => api.post('/auth/logout-all').then(unwrap),
  sessions:        ()                   => api.get('/auth/sessions').then(unwrap),
  revokeSession:   (id)                 => api.delete(`/auth/sessions/${id}`).then(unwrap),
  // Admin account hardening
  adminResetPassword: (partyId, new_password) => api.post(`/auth/admin/users/${partyId}/reset-password`, { new_password }).then(unwrap),
  adminSetActive:     (partyId, is_active)    => api.post(`/auth/admin/users/${partyId}/set-active`, { is_active }).then(unwrap),
  // Self-service onboarding (public) + admin approval queue / invites
  signup:          (d)         => api.post('/auth/signup', d).then(unwrap),
  verifySignupOtp: (email, code) => api.post('/auth/signup/verify-otp', { email, code }).then(unwrap),
  resendSignupOtp: (email)     => api.post('/auth/signup/resend-otp', { email }).then(unwrap),
  acceptInvite:    (token, password) => api.post('/auth/invites/accept', { token, password }).then(unwrap),
  pendingSignups:  ()          => api.get('/auth/admin/signups').then(unwrap),
  approveSignup:   (partyId)   => api.post(`/auth/admin/signups/${partyId}/approve`).then(unwrap),
  rejectSignup:    (partyId)   => api.post(`/auth/admin/signups/${partyId}/reject`).then(unwrap),
  createInvite:    (d)         => api.post('/auth/admin/invites', d).then(unwrap),
  // Self-service bio update (display_name, dob, gender, language, nationality, national_id)
  updateMe:        (d)                  => api.patch('/auth/me', d).then(unwrap),
  // Primary contact upsert (compat shortcut)
  upsertMyContact: (d)                  => api.put('/auth/me/contact', d).then(unwrap),
  // Full self-service contact management — parties-grade enrichment.
  // Each returns the fresh /auth/me payload (contacts[] included).
  addMyContact:    (d)                  => api.post('/auth/me/contacts', d).then(unwrap),
  updateMyContact: (id, d)              => api.patch(`/auth/me/contacts/${id}`, d).then(unwrap),
  deleteMyContact: (id)                 => api.delete(`/auth/me/contacts/${id}`).then(unwrap),
  register:        (data)               => api.post('/auth/register', data).then(unwrap),
};

// ── Party Management ──────────────────────────────────────────────────────────
export const partyAPI = {
  list:               (params) => api.get('/parties', { params }).then(unwrap),
  get:                (id)     => api.get(`/parties/${id}`).then(unwrap),
  create:             (data)   => api.post('/parties', data).then(unwrap),
  update:             (id, d)  => api.patch(`/parties/${id}`, d).then(unwrap),
  deactivate:         (id)     => api.delete(`/parties/${id}`).then(unwrap),
  addRole:            (id, role) => api.post(`/parties/${id}/roles`, { role }).then(unwrap),
  removeRole:         (id, role) => api.delete(`/parties/${id}/roles/${role}`).then(unwrap),
  addContact:         (id, d)  => api.post(`/parties/${id}/contacts`, d).then(unwrap),
  getRelationships:   (id)     => api.get(`/parties/${id}/relationships`).then(unwrap),
  createRelationship: (data)   => api.post('/parties/relationships', data).then(unwrap),
};

// ── Patients ──────────────────────────────────────────────────────────────────
export const patientAPI = {
  list:          (params) => api.get('/patients', { params }).then(unwrap),
  create:        (d)      => api.post('/patients', d).then(unwrap),
  get:           (id)     => api.get(`/patients/${id}`).then(unwrap),
  createProfile: (id, d)  => api.post(`/patients/${id}/profile`, d).then(unwrap),
  updateProfile: (id, d)  => api.patch(`/patients/${id}/profile`, d).then(unwrap),
  // P1-05 · party-core fields (name, status)
  update:        (id, d)  => api.patch(`/patients/${id}`, d).then(unwrap),
  // P1-06 · soft-delete (status -> INACTIVE)
  deactivate:    (id)     => api.delete(`/patients/${id}`).then(unwrap),
  summary:       (id)     => api.get(`/patients/${id}/summary`).then(unwrap),
  // Self-scoped record endpoints (require_roles_or_self)
  getFlags:          (id, params) => api.get(`/patients/${id}/flags`, { params }).then(unwrap),
  getPreferences:    (id)         => api.get(`/patients/${id}/preferences`).then(unwrap),
  upsertPreferences: (id, d)      => api.put(`/patients/${id}/preferences`, d).then(unwrap),
  getConsents:       (id, params) => api.get(`/patients/${id}/consents`, { params }).then(unwrap),
  // GDPR Art. 15/20 subject-access export — self or ADMIN only.
  exportData:        (id)         => api.get(`/patients/${id}/export`).then(unwrap),
};

// ── Nurses ────────────────────────────────────────────────────────────────────
export const nurseAPI = {
  list:            (params) => api.get('/nurses', { params }).then(unwrap),
  get:             (id)     => api.get(`/nurses/${id}`).then(unwrap),
  createProfile:   (id, d)  => api.post(`/nurses/${id}/profile`, d).then(unwrap),
  updateProfile:   (id, d)  => api.patch(`/nurses/${id}/profile`, d).then(unwrap),
  // P1-05 · party-core fields (name, status)
  update:          (id, d)  => api.patch(`/nurses/${id}`, d).then(unwrap),
  // P1-06 · soft-delete
  deactivate:      (id)     => api.delete(`/nurses/${id}`).then(unwrap),
  updateLocation:  (id, d)  => api.patch(`/nurses/${id}/location`, d).then(unwrap),
  setAvailability: (id, d)  => api.post(`/nurses/${id}/availability`, d).then(unwrap),
  getAvailability: (id)     => api.get(`/nurses/${id}/availability`).then(unwrap),
  getSchedule:     (id, p)  => api.get(`/nurses/${id}/schedule`, { params: p }).then(unwrap),
};

// ── Doctors & Referrals ───────────────────────────────────────────────────────
export const doctorAPI = {
  list:            (params) => api.get('/doctors', { params }).then(unwrap),
  get:             (id)     => api.get(`/doctors/${id}`).then(unwrap),
  createProfile:   (id, d)  => api.post(`/doctors/${id}/profile`, d).then(unwrap),
  updateProfile:   (id, d)  => api.patch(`/doctors/${id}/profile`, d).then(unwrap),
  // P1-05 · party-core fields (name, status)
  update:          (id, d)  => api.patch(`/doctors/${id}`, d).then(unwrap),
  // P1-06 · soft-delete
  deactivate:      (id)     => api.delete(`/doctors/${id}`).then(unwrap),
};

export const referralAPI = {
  list:   (params) => api.get('/referrals', { params }).then(unwrap),
  get:    (id)     => api.get(`/referrals/${id}`).then(unwrap),
  create: (data)   => api.post('/referrals', data).then(unwrap),
  // Atomic new-patient + referral in one call — returns credentials
  intake: (data)   => api.post('/referrals/intake', data).then(unwrap),
  update: (id, d)  => api.patch(`/referrals/${id}`, d).then(unwrap),
  cancel: (id)     => api.post(`/referrals/${id}/cancel`).then(unwrap),
  // P2-14 · REST-idiomatic alias for cancel
  delete: (id)     => api.delete(`/referrals/${id}`).then(unwrap),
};

// ── Care Requests ─────────────────────────────────────────────────────────────
export const requestAPI = {
  list:   (params) => api.get('/care-requests', { params }).then(unwrap),
  get:    (id)     => api.get(`/care-requests/${id}`).then(unwrap),
  create: (data)   => api.post('/care-requests', data).then(unwrap),
  update: (id, d)  => api.patch(`/care-requests/${id}`, d).then(unwrap),
  cancel: (id)     => api.post(`/care-requests/${id}/cancel`).then(unwrap),
  delete: (id)     => api.delete(`/care-requests/${id}`).then(unwrap),
};

// ── Dispatch Engine ───────────────────────────────────────────────────────────
// ── Episodes of Care — the clinical spine ────────────────────────────────────
export const episodeAPI = {
  list:      (params)   => api.get('/episodes/', { params }).then(unwrap),
  get:       (id)       => api.get(`/episodes/${id}`).then(unwrap),
  summary:   (id)       => api.get(`/episodes/${id}/summary`).then(unwrap),
  addGoal:   (id, d)    => api.post(`/episodes/${id}/goals`, d).then(unwrap),
  goalProgress: (id, goalId, d) => api.patch(`/episodes/${id}/goals/${goalId}`, d).then(unwrap),
  discharge: (id, d)    => api.post(`/episodes/${id}/discharge`, d).then(unwrap),
};

// ── Workforce compliance (M4) ────────────────────────────────────────────────
export const workforceAPI = {
  credentialsExpiring: (days = 90) => api.get('/workforce/credentials-expiring', { params: { days } }).then(unwrap),
};

export const dispatchAPI = {
  findNurses: (params) => api.get('/dispatch/available-nurses', { params }).then(unwrap),
  assign:     (requestId, data) => api.post(`/dispatch/${requestId}/assign`, data).then(unwrap),
  logs:       (params) => api.get('/dispatch/logs', { params }).then(unwrap),
};

// ── Branches / departments ────────────────────────────────────────────────────
export const branchAPI = {
  list:         ()          => api.get('/branches/').then(unwrap),
  create:       (data)      => api.post('/branches/', data).then(unwrap),
  update:       (id, data)  => api.patch(`/branches/${id}`, data).then(unwrap),
  members:      (id)        => api.get(`/branches/${id}/members`).then(unwrap),
  addMember:    (id, data)  => api.post(`/branches/${id}/members`, data).then(unwrap),
  removeMember: (id, pid)   => api.delete(`/branches/${id}/members/${pid}`).then(unwrap),
  workload:     (id)        => api.get(`/branches/${id}/workload`).then(unwrap),
};

// ── Visits ────────────────────────────────────────────────────────────────────
export const visitAPI = {
  list:         (params) => api.get('/visits', { params }).then(unwrap),
  get:          (id)     => api.get(`/visits/${id}`).then(unwrap),
  create:       (data)   => api.post('/visits', data).then(unwrap),
  updateStatus: (id, d)  => api.patch(`/visits/${id}/status`, d).then(unwrap),
  confirm:      (id)     => api.post(`/visits/${id}/confirm`).then(unwrap),
  update:       (id, d)  => api.patch(`/visits/${id}`, d).then(unwrap),
  cancel:       (id, reason) => api.post(`/visits/${id}/cancel`, { reason }).then(unwrap),
  // EVV check-in / check-out
  checkIn:      (id, lat, lng, override_reason) =>
    api.post(`/visits/${id}/check-in`, { latitude: lat, longitude: lng, override_reason }).then(unwrap),
  checkOut:     (id, lat, lng) =>
    api.post(`/visits/${id}/check-out`, { latitude: lat, longitude: lng }).then(unwrap),
  appendGps:    (id, lat, lng) =>
    api.post(`/visits/${id}/gps-trail`, { latitude: lat, longitude: lng }).then(unwrap),
  evvReport:    (params) => api.get('/evv/reports', { params }).then(unwrap),
  // Recurring templates
  listTemplates:   (params) => api.get('/visit-templates', { params }).then(unwrap),
  createTemplate:  (data)   => api.post('/visit-templates', data).then(unwrap),
  skipOccurrence:  (id, date) =>
    api.patch(`/visit-templates/${id}/skip`, null, { params: { occurrence_date: date } }).then(unwrap),
};

// ── Care Plans ────────────────────────────────────────────────────────────────
export const carePlanAPI = {
  list:   (params) => api.get('/care-plans', { params }).then(unwrap),
  get:    (id)     => api.get(`/care-plans/${id}`).then(unwrap),
  create: (data)   => api.post('/care-plans', data).then(unwrap),
  update: (id, d)  => api.patch(`/care-plans/${id}`, d).then(unwrap),
  // P1-06 · FHIR-compliant terminal transition. `status` defaults to REVOKED on the
  // server; pass 'COMPLETED' to close out a plan that finished naturally.
  revoke:   (id, status = 'REVOKED') => api.delete(`/care-plans/${id}`, { params: { status } }).then(unwrap),
  complete: (id)                     => api.delete(`/care-plans/${id}`, { params: { status: 'COMPLETED' } }).then(unwrap),
};

// ── Clinical Records ──────────────────────────────────────────────────────────
export const clinicalAPI = {
  submitReport:   (data)      => api.post('/clinical/reports', data).then(unwrap),
  getVisitReport: (visitId)   => api.get(`/clinical/visits/${visitId}/report`).then(unwrap),
  getReportById:  (reportId)  => api.get(`/clinical/reports/${reportId}`).then(unwrap),
  listReports:    (params)    => api.get('/clinical/reports', { params }).then(unwrap),
  updateReport:   (id, data)  => api.patch(`/clinical/reports/${id}`, data).then(unwrap),
  voidReport:     (id)        => api.delete(`/clinical/reports/${id}`).then(unwrap),
  // PDF export — returns the raw Axios response (blob + headers), NOT unwrapped,
  // so the caller can read Content-Disposition. See utils/download.js.
  reportPdf:      (id)        => api.get(`/clinical/reports/${id}/pdf`, { responseType: 'blob' }),
  // Doctor/admin electronic sign-off (part of the legal record).
  reviewReport:   (id, action, notes) =>
    api.post(`/clinical/reports/${id}/review`, { action, notes }).then(unwrap),
};

// ── Visit media (PHI photos + patient signature) ─────────────────────────────
export const visitMediaAPI = {
  list: (visitId) => api.get(`/visits/${visitId}/media`).then(unwrap),
  // Bytes stream through the authorised endpoint (bearer token via axios) —
  // a plain <img src> cannot carry the token, so callers blob these.
  fileBlob: (visitId, mediaId) =>
    api.get(`/visits/${visitId}/media/${mediaId}/file`, { responseType: 'blob' }),
};

// ── Billing ───────────────────────────────────────────────────────────────────
export const billingAPI = {
  list:    (params) => api.get('/billing', { params }).then(unwrap),
  // Create-form helpers: unbilled completed visits + the service rate card.
  unbilledVisits: () => api.get('/billing/unbilled-visits').then(unwrap),
  rates:          () => api.get('/billing/rates').then(unwrap),
  get:     (id)     => api.get(`/billing/${id}`).then(unwrap),
  create:  (data)   => api.post('/billing', data).then(unwrap),
  update:  (id, d)  => api.patch(`/billing/${id}`, d).then(unwrap),
  // P1-06 · void an invoice (reason required, persisted in payment_reference).
  // Distinct from REFUNDED — void is for invoices issued in error.
  void:    (id, reason) => api.delete(`/billing/${id}`, { params: { reason } }).then(unwrap),
  summary: ()       => api.get('/billing/summary').then(unwrap),
  // Invoice PDF — returns the raw Axios response (blob + headers), NOT unwrapped,
  // so the caller can read Content-Disposition. See utils/download.js.
  pdf:     (id)     => api.get(`/billing/${id}/pdf`, { responseType: 'blob' }),
};

// ── Family / caregiver access (consent-scoped, patient-controlled) ───────────
export const familyAccessAPI = {
  // GET /patients/{id}/family-access → list of grants for this patient
  list:   (patientId)          => api.get(`/patients/${patientId}/family-access`).then(unwrap),
  // POST /patients/{id}/family-access → create a grant
  grant:  (patientId, data)    => api.post(`/patients/${patientId}/family-access`, data).then(unwrap),
  // DELETE /patients/{id}/family-access/{grantId} → revoke a grant
  revoke: (patientId, grantId) => api.delete(`/patients/${patientId}/family-access/${grantId}`).then(unwrap),
};

// ── Analytics ─────────────────────────────────────────────────────────────────
export const analyticsAPI = {
  dashboard:        ()           => api.get('/analytics/dashboard').then(unwrap),
  visitsTrend:      (days = 30)  => api.get('/analytics/visits-trend', { params: { days } }).then(unwrap),
  revenueTrend:     (months = 6) => api.get('/analytics/revenue-trend', { params: { months } }).then(unwrap),
  serviceBreakdown: ()           => api.get('/analytics/service-breakdown').then(unwrap),
  nursePerformance: (limit = 10) => api.get('/analytics/nurse-performance', { params: { limit } }).then(unwrap),
};

// ── Notifications ─────────────────────────────────────────────────────────────
export const notificationAPI = {
  list:         (params) => api.get('/notifications', { params }).then(unwrap),
  unreadCount:  ()       => api.get('/notifications/unread-count').then(unwrap),
  markRead:     (id)     => api.patch(`/notifications/${id}/read`).then(unwrap),
  markAllRead:  ()       => api.post('/notifications/mark-all-read').then(unwrap),
};

// ── Organizations ─────────────────────────────────────────────────────────────
export const orgAPI = {
  list:          (params) => api.get('/organizations/', { params }).then(unwrap),
  get:           (id)     => api.get(`/organizations/${id}`).then(unwrap),
  createProfile: (id, d)  => api.post(`/organizations/${id}/profile`, d).then(unwrap),
  updateProfile: (id, d)  => api.patch(`/organizations/${id}/profile`, d).then(unwrap),
  // P1-01 · party-core fields (name, status)
  update:        (id, d)  => api.patch(`/organizations/${id}`, d).then(unwrap),
  deactivate:    (id)     => api.delete(`/organizations/${id}`).then(unwrap),
};

// ── Prescriptions ─────────────────────────────────────────────────────────────
export const prescriptionAPI = {
  list:         (params)  => api.get('/prescriptions', { params }).then(unwrap),
  get:          (id)      => api.get(`/prescriptions/${id}`).then(unwrap),
  create:       (data)    => api.post('/prescriptions', data).then(unwrap),
  update:       (id, d)   => api.patch(`/prescriptions/${id}`, d).then(unwrap),
  updateStatus: (id, s)   => api.patch(`/prescriptions/${id}/status`, null, { params: { status: s } }).then(unwrap),
  discontinue:  (id)      => api.delete(`/prescriptions/${id}`).then(unwrap),
};

// ── Remote Observations ───────────────────────────────────────────────────────
export const remoteObsAPI = {
  list:          (params) => api.get('/remote-observations', { params }).then(unwrap),
  get:           (id)     => api.get(`/remote-observations/${id}`).then(unwrap),
  create:        (data)   => api.post('/remote-observations', data).then(unwrap),
  update:        (id, d)  => api.patch(`/remote-observations/${id}`, d).then(unwrap),
  delete:        (id)     => api.delete(`/remote-observations/${id}`).then(unwrap),
  latestVitals:  (patientId) => api.get(`/remote-observations/patient/${patientId}/latest`).then(unwrap),
};
